import { Editor, Path, type PathRef, Range } from 'slate';
import { getTableStructureFromElement } from './TableStructure';
import TableUtils from './TableUtils';
import TableRange from './TableRange';
import { isTableElement } from '../../types/guards';

class TableSelection {
  editor: Editor;
  tablePathRef: PathRef | null;
  tableRange: TableRange | null;
  anchorCellPath: Path | null;
  focusCellPath: Path | null;
  selectedTables: Set<string>;
  selectedCells: Set<string>;
  isUpdatingSelection: boolean;
  shouldClearSelection: boolean;

  constructor({
    editor,
    tablePath,
    tableRange,
    anchorCellPath,
    focusCellPath,
    selectedTables,
    selectedCells,
    isUpdatingSelection,
    shouldClearSelection,
  }: {
    editor: Editor;
    tablePath?: Path | null;
    tableRange?: TableRange | null;
    anchorCellPath?: Path | null;
    focusCellPath?: Path | null;
    selectedTables?: Set<string>;
    selectedCells?: Set<string>;
    isUpdatingSelection?: boolean;
    shouldClearSelection?: boolean;
  }) {
    this.editor = editor;
    this.tablePathRef = tablePath ? Editor.pathRef(editor, tablePath) : null;
    this.tableRange = tableRange ?? null;
    this.anchorCellPath = anchorCellPath ?? null;
    this.focusCellPath = focusCellPath ?? null;
    this.selectedTables = selectedTables ?? new Set();
    this.selectedCells = selectedCells ?? new Set();
    this.isUpdatingSelection = isUpdatingSelection ?? false;
    this.shouldClearSelection = shouldClearSelection ?? false;
  }

  get multipleCellsSelected() {
    // selectedCells will only contain items if more than one cell
    // is selected, hence the check for > 0 rather than > 1.
    return this.selectedCells.size > 0;
  }

  get selectedCellPaths() {
    return new Set(
      Array.from(this.selectedCells).map((cell) => cell.split(',').map(Number))
    );
  }

  get height() {
    return this.tableRange?.size.rows ?? 0;
  }

  get width() {
    return this.tableRange?.size.columns ?? 0;
  }

  get tablePath() {
    if (this.tablePathRef) {
      return this.tablePathRef.current;
    }
    return null;
  }

  get startCellPath() {
    return this.tableRange?.isBackward
      ? this.focusCellPath
      : this.anchorCellPath;
  }

  get endCellPath() {
    return this.tableRange?.isBackward
      ? this.anchorCellPath
      : this.focusCellPath;
  }

  isTableSelected(path: Path) {
    return this.selectedTables.has(path.toString());
  }

  tableHasSelectedCells(path: Path) {
    return (
      this.isTableSelected(path) ||
      (this.tablePath && Path.equals(this.tablePath, path))
    );
  }

  isCellSelected(tablePath: Path, path: Path) {
    return (
      this.tablePath &&
      Path.equals(this.tablePath, tablePath) &&
      this.selectedCells.has(path.toString())
    );
  }

  get hasWholeTableSelection() {
    return this.selectedTables.size > 0;
  }

  getTableStructure(editor: Editor) {
    if (!this.tablePath) {
      return null;
    }

    const tableElement = Editor.node(editor, this.tablePath);
    if (!tableElement || !isTableElement(tableElement[0])) {
      return null;
    }

    return getTableStructureFromElement(editor, tableElement[0]);
  }

  refreshTableSelection(editor: Editor) {
    const { selection } = editor;
    if (!selection) return;

    this.extendSelectionToPath(editor, selection, selection?.focus.path);
  }

  updateCellSelection(editor: Editor) {
    if (this.hasWholeTableSelection) {
      return;
    }

    if (!this.tablePath) {
      this.reset();
      return;
    }

    if (!this.anchorCellPath || !this.focusCellPath) return;

    const table = Editor.node(editor, this.tablePath);
    const anchorCell = Editor.node(editor, this.anchorCellPath);
    const focusCell = Editor.node(editor, this.focusCellPath);

    if (!table || !anchorCell || !focusCell) {
      this.reset();
      return;
    }

    const tableStructure = this.getTableStructure(editor);

    if (!tableStructure) return;

    const anchorPosition = tableStructure.pathToCoordinates(
      anchorCell[1].slice(-2)
    );
    const focusPosition = tableStructure.pathToCoordinates(
      focusCell[1].slice(-2)
    );
    const tableRange = new TableRange(anchorPosition, focusPosition);

    this.tableRange = tableRange;
    this.selectedCells = tableStructure.cellPathsInRange(tableRange);
  }

  extendSelectionToPath(editor: Editor, selection: Range, targetPath: Path) {
    if (
      !selection ||
      (targetPath &&
        Path.equals(selection.focus.path, targetPath) &&
        Range.isCollapsed(selection))
    ) {
      this.reset();
    }

    const selectedTables = this.getSelectedTablesAtRange(
      editor,
      selection.anchor.path,
      targetPath
    );

    if (selectedTables.size > 0) {
      this.reset();
      this.selectedTables = selectedTables;
    } else {
      const tableSelection = this.getTableSelectionAtRange(
        editor,
        selection.anchor.path,
        targetPath
      );

      if (this.tablePathRef?.current) this.tablePathRef.unref();

      this.tablePathRef = tableSelection?.tablePath
        ? Editor.pathRef(this.editor, tableSelection.tablePath)
        : null;
      this.anchorCellPath = tableSelection?.anchorCellPath ?? null;
      this.focusCellPath = tableSelection?.focusCellPath ?? null;
      this.selectedTables.clear();
    }

    this.updateCellSelection(editor);
  }

  getTableSelectionAtRange(editor: Editor, anchorPath: Path, focusPath: Path) {
    if (Path.equals(anchorPath, focusPath)) {
      return;
    }

    let anchorTableCell = TableUtils.parentCell(editor, anchorPath);
    let focusTableCell = TableUtils.parentCell(editor, focusPath);

    if (!anchorTableCell || !focusTableCell) {
      return;
    }

    if (Path.equals(anchorTableCell[1], focusTableCell[1])) {
      return;
    }

    let anchorTable = TableUtils.parentTable(editor, anchorPath);
    let focusTable = TableUtils.parentTable(editor, focusPath);

    if (!anchorTable || !focusTable) {
      return;
    }

    if (Path.isAncestor(anchorTable[1], focusTable[1])) {
      // Is focus table within anchor table?
      focusTable = anchorTable;
      focusTableCell = TableUtils.tableCellInTable(
        editor,
        anchorTable[1],
        focusTableCell[1]
      );
    } else if (Path.isAncestor(focusTable[1], anchorTable[1])) {
      // Is anchor table within focus table?
      anchorTable = focusTable;
      anchorTableCell = TableUtils.tableCellInTable(
        editor,
        focusTable[1],
        anchorTableCell[1]
      );
    }

    if (!Path.equals(anchorTable[1], focusTable[1])) {
      return;
    }

    return {
      tablePath: anchorTable[1],
      anchorCellPath: anchorTableCell?.[1],
      focusCellPath: focusTableCell?.[1],
    };
  }

  getSelectedTablesAtRange(editor: Editor, anchorPath: Path, focusPath: Path) {
    const selectedTables = new Set<string>();

    // Selection is within the same cell
    if (Path.equals(anchorPath, focusPath)) {
      return selectedTables;
    }

    const anchorTable = TableUtils.parentTable(editor, anchorPath);
    const focusTable = TableUtils.parentTable(editor, focusPath);

    // Selection is within the same table
    if (
      anchorTable &&
      focusTable &&
      Path.equals(anchorTable[1], focusTable[1])
    ) {
      return selectedTables;
    }

    if (anchorTable) {
      selectedTables.add(anchorTable[1].toString());
    }

    if (focusTable) {
      selectedTables.add(focusTable[1].toString());
    }

    const tablesInRange = Array.from(
      Editor.nodes(editor, {
        at: {
          anchor: { path: anchorPath, offset: 0 },
          focus: { path: focusPath, offset: 0 },
        },
        match: isTableElement,
      })
    );

    for (const [, path] of tablesInRange) {
      selectedTables.add(path.toString());
    }

    return selectedTables;
  }

  reset() {
    if (this.tablePathRef) this.tablePathRef.unref();
    this.tablePathRef = null;
    this.tableRange = null;
    this.anchorCellPath = null;
    this.focusCellPath = null;
    this.selectedTables.clear();
    this.selectedCells.clear();
    this.isUpdatingSelection = false;
    this.shouldClearSelection = false;
  }
}

export default TableSelection;
